import { toExcelDate } from 'components/xlsx/excelCells';
import type ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import { PrepaymentReceiptRow, SalesJournalResponse, SalesJournalRow } from 'types/reports';

/** Number format shared by every numeric cell: 2 decimals, negatives in red parentheses. */
const NUMBER_FORMAT = '#,##0.00 ;[Red](#,##0.00)';

/** First worksheet row that holds journal data (after title/period/source/blank/header rows). */
const DATA_FIRST = 6;

/** Standard VAT rate, shown in the sheet's informational rate cell. Splits test the VAT amount, never this rate. */
const VAT_RATE = 0.24;

/** Region labels in the fixed Specification ordering. */
const REGION_HOME = 'Home Country';
const REGION_EU = 'EU';
const REGION_OTHER = 'Other';
const REGION_ORDER = [REGION_HOME, REGION_EU, REGION_OTHER] as const;
type Region = (typeof REGION_ORDER)[number];

/** VAT-base labels. The zero-rated base differs by region; "VAT 24%" is shared. */
const BASE_VAT_24 = 'VAT 24%';
const BASE_TAX_FREE = 'Tax-Free';
const BASE_VAT_0 = 'VAT 0%';

/**
 * Sheet "Sales Journal" column layout. Columns A..M are visible; N..AH are hidden helper columns
 * grouped under one outline level. Each entry is keyed by its 1-based column index for clarity.
 */
const SALES_JOURNAL_COLUMNS = [
    { header: 'Invoice', width: 18, hidden: false },
    { header: 'Entry Date', width: 14, hidden: false },
    { header: 'Payer', width: 28, hidden: false },
    { header: 'VAT Number', width: 16, hidden: false },
    { header: 'Status', width: 14, hidden: false },
    { header: 'Expenses', width: 14, hidden: false },
    { header: 'Fees', width: 14, hidden: false },
    { header: 'Discount', width: 14, hidden: false },
    { header: 'Total', width: 14, hidden: false },
    { header: 'Advances', width: 14, hidden: false },
    { header: 'Net', width: 14, hidden: false },
    { header: 'VAT', width: 14, hidden: false },
    { header: 'Total (gross)', width: 16, hidden: false },
    { header: 'Region', width: 14, hidden: true },
    { header: 'Exp net (no VAT)', width: 16, hidden: true },
    { header: 'Exp net (VAT)', width: 16, hidden: true },
    { header: 'Exp VAT', width: 14, hidden: true },
    { header: 'Fee net', width: 14, hidden: true },
    { header: 'Fee VAT', width: 14, hidden: true },
    { header: 'Net (raw)', width: 14, hidden: true },
    { header: 'VAT (raw)', width: 14, hidden: true },
    { header: 'Gross (raw)', width: 14, hidden: true },
    { header: 'Adv gross', width: 14, hidden: true },
    { header: 'Rate', width: 12, hidden: true },
    { header: 'Adv net', width: 14, hidden: true },
    { header: 'Adv VAT', width: 14, hidden: true },
    { header: 'Fee net (VAT)', width: 14, hidden: true },
    { header: 'Fee net (0%)', width: 14, hidden: true },
    { header: 'Type', width: 14, hidden: true },
    { header: 'Inv VAT base', width: 14, hidden: true },
    { header: 'Recv adv net', width: 14, hidden: true },
    { header: 'Recv adv VAT', width: 14, hidden: true },
    { header: 'Exp net (VAT 24%)', width: 18, hidden: true },
    { header: 'Exp net (VAT 0%)', width: 18, hidden: true },
] as const;

/** Returns 0 for non-finite values (covers nulls coerced via the base-currency guard). */
const num = (value: number): number => (Number.isFinite(value) ? value : 0);

/**
 * Rounds to 2 decimals half-up. Invoice helper columns round via the Excel ROUND() formula, but received-advance
 * net/VAT are JS-computed and written as plain numbers, so they need the same half-up rounding applied here. The
 * epsilon nudge offsets binary float representation error so values like 2.345 round up rather than to even.
 */
const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

/** Maps a backend region code to its journal label; unknown codes fall back to "Other". */
const regionLabel = (countryLocationTypeCode: string): Region => {
    switch (countryLocationTypeCode) {
        case 'DOMESTIC':
            return REGION_HOME;
        case 'FOREIGN_EU':
            return REGION_EU;
        case 'FOREIGN':
            return REGION_OTHER;
        default:
            return REGION_OTHER;
    }
};

/**
 * Classifies a row's VAT base: any nonzero invoice VAT means standard-rate ("VAT 24%") — a credit note of
 * a standard-rated invoice carries negative VAT yet is still "VAT 24%". A zero-VAT domestic invoice is
 * tax-free and a zero-VAT foreign one is 0%-rated.
 */
const vatBase = (invoiceVatRaw: number, countryLocationTypeCode: string): string => {
    if (invoiceVatRaw !== 0) {
        return BASE_VAT_24;
    }
    return countryLocationTypeCode === 'DOMESTIC' ? BASE_TAX_FREE : BASE_VAT_0;
};

/**
 * Normalized journal row: all raw numeric inputs (O..W), the region label (N), the type (AC), the VAT base
 * (AD) and the received-advance net/VAT (AE/AF), pre-extracted from the backend row. Holding these as plain
 * fields keeps the cell-writing loop trivial. Invoice rows carry recvAdvNet/recvAdvVat = 0; received-advance
 * receipt rows carry the receipt's net/VAT here and zero out every invoice raw input.
 */
interface NormalizedRow {
    invoiceNumber: string;
    invoiceDate: string;
    payer: string;
    vatRegNum: string;
    status: string;
    region: Region;
    type: string;
    base: string;
    expNetNoVat: number;
    expNetVat: number;
    expVat: number;
    feeNet: number;
    feeVat: number;
    netRaw: number;
    vatRaw: number;
    grossRaw: number;
    advGross: number;
    recvAdvNet: number;
    recvAdvVat: number;
}

/**
 * Per-row values computed in JS to mirror the worksheet's display and helper formulas exactly. These feed
 * the cached `result` of each formula cell (so non-recalculating apps show correct numbers) and are summed
 * to produce the SUMMARY and Specification cached results.
 */
interface ComputedRow {
    type: string;
    region: Region;
    base: string;
    expNetNoVat: number;
    expNetVat24: number;
    expNetVat0: number;
    expVat: number;
    feeVat: number;
    feeNetVat: number;
    feeNet0: number;
    advNet: number;
    advVat: number;
    recvAdvNet: number;
    recvAdvVat: number;
    F: number;
    G: number;
    H: number;
    I: number;
    J: number;
    K: number;
    L: number;
    M: number;
}

/** Extracts the raw base-currency inputs from a backend row, guarding the nullable block to zeros. */
const normalizeRow = (row: SalesJournalRow): NormalizedRow => {
    const base = row.baseCurrency;
    const vatRaw = num(base?.invoice.vat ?? 0);
    return {
        invoiceNumber: row.invoiceNumber,
        invoiceDate: row.invoiceDate,
        payer: row.clientPartyName,
        vatRegNum: row.vatRegNum,
        status: row.invoiceStatusTypeCode,
        region: regionLabel(row.countryLocationTypeCode),
        type: row.invoiceTypeCode,
        base: vatBase(vatRaw, row.countryLocationTypeCode),
        expNetNoVat: num(base?.expensesWithoutVat.totalWithoutVat ?? 0),
        expNetVat: num(base?.expensesWithVat.totalWithoutVat ?? 0),
        expVat: num(base?.expensesWithVat.vat ?? 0),
        feeNet: num(base?.fees.totalWithoutVat ?? 0),
        feeVat: num(base?.fees.vat ?? 0),
        netRaw: num(base?.invoice.totalWithoutVat ?? 0),
        vatRaw,
        grossRaw: num(base?.invoice.totalWithVat ?? 0),
        advGross: num(base?.invoice.prepaymentAmount ?? 0),
        recvAdvNet: 0,
        recvAdvVat: 0,
    };
};

/**
 * Normalizes a received-advance receipt into a journal row. Its period key (Entry Date / column B) is the
 * PAYMENT date, not the underlying invoice date. The receipt carries only its net/VAT split in the AE/AF
 * helper columns — every invoice raw input (O..W) and the derived invoice net/VAT/gross are zeroed so the
 * rewired display formulas surface only the received advance on these rows. The net/VAT split uses the
 * advance's OWN VAT rate (from its base-currency invoice totals), guarding a zero net against divide-by-zero.
 */
const normalizeReceipt = (receipt: PrepaymentReceiptRow): NormalizedRow => {
    const base = receipt.baseCurrency;
    const invoiceNet = num(base?.invoice.totalWithoutVat ?? 0);
    const invoiceVat = num(base?.invoice.vat ?? 0);
    const rate = invoiceNet !== 0 ? invoiceVat / invoiceNet : 0;
    const gross = num(receipt.paymentAmountBaseCurrency);
    const net = round2(gross / (1 + rate));
    const recvVat = round2(gross - net);

    const receiptBase =
        rate !== 0 ? BASE_VAT_24 : receipt.countryLocationTypeCode === 'DOMESTIC' ? BASE_TAX_FREE : BASE_VAT_0;

    return {
        invoiceNumber: receipt.invoiceNumber,
        invoiceDate: receipt.paymentDate,
        payer: receipt.clientPartyName,
        vatRegNum: receipt.vatRegNum,
        status: receipt.invoiceStatusTypeCode,
        region: regionLabel(receipt.countryLocationTypeCode),
        type: 'PREPAYMENT',
        base: receiptBase,
        expNetNoVat: 0,
        expNetVat: 0,
        expVat: 0,
        feeNet: 0,
        feeVat: 0,
        netRaw: 0,
        vatRaw: 0,
        grossRaw: 0,
        advGross: 0,
        recvAdvNet: net,
        recvAdvVat: recvVat,
    };
};

/** Formats an ISO date (yyyy-MM-dd) as dd.MM.yyyy for the period header line. */
const formatPeriodDate = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) {
        return isoDate;
    }
    return `${day}.${month}.${year}`;
};

/** Sets the shared 2-decimal number format on a single cell. */
const formatNumeric = (cell: ExcelJS.Cell): void => {
    cell.numFmt = NUMBER_FORMAT;
};

/**
 * Writes a formula cell (ExcelJS formula strings exclude the leading "="). The exact JS-computed `result`
 * is cached so a spreadsheet app that does not recalculate on load (e.g. LibreOffice) still displays the
 * correct value; the formula string is kept so live editing recalculates the same number.
 */
const setFormula = (cell: ExcelJS.Cell, formula: string, result: number): void => {
    cell.value = { formula, result };
    formatNumeric(cell);
};

/** Builds the "Sales Journal" sheet with the visible display formulas, hidden helper columns and SUMMARY block. */
const buildSalesJournalSheet = (
    workbook: ExcelJS.Workbook,
    rows: NormalizedRow[],
    periodLabel: string
): ComputedRow[] => {
    const sheet = workbook.addWorksheet('Sales Journal');
    const dataLast = DATA_FIRST + rows.length - 1;
    const range = (column: string): string => `$${column}$${DATA_FIRST}:$${column}$${dataLast}`;

    sheet.columns = SALES_JOURNAL_COLUMNS.map((column) => ({ width: column.width }));
    SALES_JOURNAL_COLUMNS.forEach((column, index) => {
        if (column.hidden) {
            const worksheetColumn = sheet.getColumn(index + 1);
            worksheetColumn.hidden = true;
            worksheetColumn.outlineLevel = 1;
        }
    });
    sheet.properties.outlineLevelCol = 1;

    sheet.getCell('A1').value = 'Sales Journal (KMD)';
    sheet.getCell('N1').value = 'VAT rate';
    sheet.getCell('O1').value = VAT_RATE;
    sheet.getCell('A2').value = `Period: ${periodLabel}`;
    sheet.getCell('A3').value = 'Source: Sales Journal (KMD)';

    const headerRow = sheet.getRow(5);
    SALES_JOURNAL_COLUMNS.forEach((column, index) => {
        headerRow.getCell(index + 1).value = column.header;
    });
    headerRow.font = { bold: true };

    const computed: ComputedRow[] = [];

    rows.forEach((row, index) => {
        const r = DATA_FIRST + index;
        const sheetRow = sheet.getRow(r);

        // Mirror the hidden helper-column formulas (X..AB, AG..AH) in JS so every formula cell carries its exact value.
        const rate = row.netRaw !== 0 ? row.vatRaw / row.netRaw : 0; // X = IF(T=0,0,U/T)
        const advNet = row.advGross !== 0 ? round2(row.advGross / (1 + rate)) : 0; // Y
        const advVat = round2(row.advGross - advNet); // Z
        // Within one invoice a with-VAT bucket is all-or-nothing: nonzero VAT means the whole bucket is
        // standard-rated, zero VAT means it is 0%-rated (reverse charge). Testing the VAT amount keeps the
        // net exact and rate-agnostic, where dividing it by a fixed rate both drifts and assumes 24%.
        const feeNetVat = row.feeVat !== 0 ? row.feeNet : 0; // AA
        const feeNet0 = row.feeNet - feeNetVat; // AB
        const expNetVat24 = row.expVat !== 0 ? row.expNetVat : 0; // AG
        const expNetVat0 = row.expNetVat - expNetVat24; // AH

        // Visible display columns (F..M).
        const F = row.expNetNoVat + row.expNetVat;
        const G = row.feeNet;
        const H = round2(row.expNetNoVat + row.expNetVat + row.feeNet - row.netRaw);
        const I = row.netRaw;
        const J = row.recvAdvNet - advNet;
        const K = row.netRaw - advNet + row.recvAdvNet;
        const L = row.vatRaw - advVat + row.recvAdvVat;
        const M = row.grossRaw - row.advGross + row.recvAdvNet + row.recvAdvVat;

        sheetRow.getCell(1).value = row.invoiceNumber;
        sheetRow.getCell(2).value = toExcelDate(row.invoiceDate);
        sheetRow.getCell(3).value = row.payer;
        sheetRow.getCell(4).value = row.vatRegNum;
        sheetRow.getCell(5).value = row.status;

        setFormula(sheetRow.getCell(6), `O${r}+P${r}`, F);
        setFormula(sheetRow.getCell(7), `R${r}`, G);
        setFormula(sheetRow.getCell(8), `ROUND(O${r}+P${r}+R${r}-T${r},2)`, H);
        setFormula(sheetRow.getCell(9), `T${r}`, I);
        setFormula(sheetRow.getCell(10), `AE${r}-Y${r}`, J);
        setFormula(sheetRow.getCell(11), `T${r}-Y${r}+AE${r}`, K);
        setFormula(sheetRow.getCell(12), `U${r}-Z${r}+AF${r}`, L);
        setFormula(sheetRow.getCell(13), `V${r}-W${r}+AE${r}+AF${r}`, M);

        sheetRow.getCell(14).value = row.region;
        const rawValues: [number, number][] = [
            [15, row.expNetNoVat],
            [16, row.expNetVat],
            [17, row.expVat],
            [18, row.feeNet],
            [19, row.feeVat],
            [20, row.netRaw],
            [21, row.vatRaw],
            [22, row.grossRaw],
            [23, row.advGross],
        ];
        rawValues.forEach(([columnIndex, value]) => {
            const cell = sheetRow.getCell(columnIndex);
            cell.value = value;
            formatNumeric(cell);
        });

        setFormula(sheetRow.getCell(24), `IF(T${r}=0,0,U${r}/T${r})`, rate);
        setFormula(sheetRow.getCell(25), `IF(W${r}=0,0,ROUND(W${r}/(1+X${r}),2))`, advNet);
        setFormula(sheetRow.getCell(26), `ROUND(W${r}-Y${r},2)`, advVat);
        setFormula(sheetRow.getCell(27), `IF(S${r}=0,0,R${r})`, feeNetVat);
        setFormula(sheetRow.getCell(28), `R${r}-AA${r}`, feeNet0);

        sheetRow.getCell(29).value = row.type;
        sheetRow.getCell(30).value = row.base;

        const recvValues: [number, number][] = [
            [31, row.recvAdvNet],
            [32, row.recvAdvVat],
        ];
        recvValues.forEach(([columnIndex, value]) => {
            const cell = sheetRow.getCell(columnIndex);
            cell.value = value;
            formatNumeric(cell);
        });

        setFormula(sheetRow.getCell(33), `IF(Q${r}=0,0,P${r})`, expNetVat24);
        setFormula(sheetRow.getCell(34), `P${r}-AG${r}`, expNetVat0);

        computed.push({
            type: row.type,
            region: row.region,
            base: row.base,
            expNetNoVat: row.expNetNoVat,
            expNetVat24,
            expNetVat0,
            expVat: row.expVat,
            feeVat: row.feeVat,
            feeNetVat,
            feeNet0,
            advNet,
            advVat,
            recvAdvNet: row.recvAdvNet,
            recvAdvVat: row.recvAdvVat,
            F,
            G,
            H,
            I,
            J,
            K,
            L,
            M,
        });
    });

    const summaryHeaderRow = dataLast + 3;
    const summaryColumns = ['F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'];
    const summaryLabels = ['Expenses', 'Fees', 'Discount', 'Total', 'Advances', 'Net', 'VAT', 'Total (gross)'];

    sheet.getCell(`C${dataLast + 2}`).value = 'SUMMARY';
    summaryColumns.forEach((column, index) => {
        sheet.getCell(`${column}${summaryHeaderRow}`).value = summaryLabels[index];
    });

    sheet.getCell(`C${summaryHeaderRow + 1}`).value = 'Credit';
    sheet.getCell(`C${summaryHeaderRow + 2}`).value = 'Normal';
    sheet.getCell(`C${summaryHeaderRow + 3}`).value = 'Prepayments';
    sheet.getCell(`C${summaryHeaderRow + 4}`).value = 'Total';

    const sumWhere = (column: keyof ComputedRow, predicate: (row: ComputedRow) => boolean): number =>
        computed.reduce((sum, row) => (predicate(row) ? sum + (row[column] as number) : sum), 0);

    summaryColumns.forEach((column) => {
        const key = column as keyof ComputedRow;
        setFormula(
            sheet.getCell(`${column}${summaryHeaderRow + 1}`),
            `SUMIF(${range('AC')},"CREDIT",${range(column)})`,
            sumWhere(key, (row) => row.type === 'CREDIT')
        );
        setFormula(
            sheet.getCell(`${column}${summaryHeaderRow + 2}`),
            `SUMIF(${range('AC')},"DEBIT",${range(column)})`,
            sumWhere(key, (row) => row.type === 'DEBIT')
        );
        setFormula(
            sheet.getCell(`${column}${summaryHeaderRow + 3}`),
            `SUMIF(${range('AC')},"PREPAYMENT",${range(column)})`,
            sumWhere(key, (row) => row.type === 'PREPAYMENT')
        );
        setFormula(
            sheet.getCell(`${column}${summaryHeaderRow + 4}`),
            `SUM(${range(column)})`,
            sumWhere(key, () => true)
        );
    });

    [dataLast + 2, summaryHeaderRow, summaryHeaderRow + 4].forEach((rowNumber) => {
        sheet.getRow(rowNumber).font = { bold: true };
    });

    return computed;
};

/** A (region, base) combination the Specification prints a row for, in the fixed Specification order. */
interface SpecificationGroup {
    region: Region;
    base: string;
}

/** The region's zero-rated base: "Tax-Free" at home, "VAT 0%" for EU and Other. */
const zeroBaseForRegion = (region: Region): string => (region === REGION_HOME ? BASE_TAX_FREE : BASE_VAT_0);

/** Bases within a region, zero-rated first then standard rate, restricted to `presentBases`. */
const orderedBasesForRegion = (region: Region, presentBases: Set<string>): string[] =>
    [zeroBaseForRegion(region), BASE_VAT_24].filter((base) => presentBases.has(base));

/**
 * Collects the (region, base) groups to print, ordered by region then by base: every base some row carries, plus
 * the region's zero-rated base whenever that row's net amounts are non-zero to the cent, even if every invoice in
 * the region bears VAT.
 */
const collectSpecificationGroups = (rows: ComputedRow[]): SpecificationGroup[] => {
    const basesByRegion = new Map<Region, Set<string>>();
    // The zero-rated row's Expenses Net (C) and Fees Net (E), summed region-wide as writeMemberRow sums them.
    const zeroNetsByRegion = new Map<Region, { expenses: number; fees: number }>();
    rows.forEach((row) => {
        const bases = basesByRegion.get(row.region) ?? new Set<string>();
        bases.add(row.base);
        basesByRegion.set(row.region, bases);
        const zeroNets = zeroNetsByRegion.get(row.region) ?? { expenses: 0, fees: 0 };
        zeroNets.expenses += row.expNetNoVat + row.expNetVat0;
        zeroNets.fees += row.feeNet0;
        zeroNetsByRegion.set(row.region, zeroNets);
    });

    const groups: SpecificationGroup[] = [];
    REGION_ORDER.forEach((region) => {
        const presentBases = basesByRegion.get(region);
        if (!presentBases) {
            return;
        }
        const zeroNets = zeroNetsByRegion.get(region);
        if (zeroNets && (round2(zeroNets.expenses) !== 0 || round2(zeroNets.fees) !== 0)) {
            presentBases.add(zeroBaseForRegion(region));
        }
        orderedBasesForRegion(region, presentBases).forEach((base) => {
            groups.push({ region, base });
        });
    });
    return groups;
};

/** The eight Specification value columns C..J (Expenses/Fees/Advances net+VAT, then Total net+VAT). */
type SpecColumns = { C: number; D: number; E: number; F: number; G: number; H: number; I: number; J: number };

/** Builds the "Specification" cross-tab sheet: per (region, base) rows, region subtotals and a grand total. */
const buildSpecificationSheet = (
    workbook: ExcelJS.Workbook,
    computed: ComputedRow[],
    dataLast: number,
    periodLabel: string
): void => {
    const sheet = workbook.addWorksheet('Specification');
    const range = (column: string): string => `'Sales Journal'!$${column}$${DATA_FIRST}:$${column}$${dataLast}`;

    sheet.columns = [
        { width: 18 },
        { width: 12 },
        { width: 16 },
        { width: 16 },
        { width: 16 },
        { width: 16 },
        { width: 16 },
        { width: 16 },
        { width: 16 },
        { width: 16 },
    ];

    sheet.getCell('A1').value = 'Specification of tax-free and tax-inclusive sales';
    sheet.getCell('A2').value = `Period: ${periodLabel}`;

    const headerRow = sheet.getRow(4);
    [
        "Payer's Area",
        'VAT Base',
        'Expenses Net',
        'Expenses VAT',
        'Fees Net',
        'Fees VAT',
        'Advances Net',
        'Advances VAT',
        'Total Net',
        'Total VAT',
    ].forEach((label, index) => {
        headerRow.getCell(index + 1).value = label;
    });
    headerRow.font = { bold: true };

    const groups = collectSpecificationGroups(computed);

    // Per-region sum across ALL bases (matches the by-region SUMIF over the helper column).
    const sumRegion = (region: Region, key: keyof ComputedRow): number =>
        computed.reduce((sum, row) => (row.region === region ? sum + (row[key] as number) : sum), 0);
    // Per (region, base) sum (matches the SUMIFS on region AND base).
    const sumRegionBase = (region: Region, base: string, key: keyof ComputedRow): number =>
        computed.reduce(
            (sum, row) => (row.region === region && row.base === base ? sum + (row[key] as number) : sum),
            0
        );

    // Track the worksheet row of each member row, grouped by region, so subtotals and the grand total
    // can reference the exact member rows (never the subtotal rows).
    const memberRowsByRegion = new Map<Region, number[]>();
    const allMemberRows: number[] = [];
    // Per-member computed C..J values, keyed by worksheet row, so subtotals and the grand total cache the
    // exact sums of the member results.
    const memberValuesByRow = new Map<number, SpecColumns>();
    let currentRow = 5;

    const sumMemberColumns = (memberRows: number[], column: keyof SpecColumns): number =>
        memberRows.reduce((sum, memberRow) => sum + (memberValuesByRow.get(memberRow)?.[column] ?? 0), 0);

    const writeMemberRow = (group: SpecificationGroup): void => {
        const r = currentRow;
        const sheetRow = sheet.getRow(r);
        sheetRow.getCell(1).value = group.region;
        sheetRow.getCell(2).value = group.base;

        const isVat24 = group.base === BASE_VAT_24;
        const C = isVat24
            ? sumRegion(group.region, 'expNetVat24')
            : sumRegion(group.region, 'expNetNoVat') + sumRegion(group.region, 'expNetVat0');
        const D = isVat24 ? sumRegion(group.region, 'expVat') : 0;
        const E = isVat24 ? sumRegion(group.region, 'feeNetVat') : sumRegion(group.region, 'feeNet0');
        const FValue = isVat24 ? sumRegion(group.region, 'feeVat') : 0;
        const G =
            -sumRegionBase(group.region, group.base, 'advNet') + sumRegionBase(group.region, group.base, 'recvAdvNet');
        const H =
            -sumRegionBase(group.region, group.base, 'advVat') + sumRegionBase(group.region, group.base, 'recvAdvVat');
        const I = C + E + G;
        const J = D + FValue + H;

        const regionMatch = `${range('N')},"${group.region}"`;
        if (isVat24) {
            setFormula(sheetRow.getCell(3), `SUMIF(${regionMatch},${range('AG')})`, C);
            setFormula(sheetRow.getCell(4), `SUMIF(${regionMatch},${range('Q')})`, D);
            setFormula(sheetRow.getCell(5), `SUMIF(${regionMatch},${range('AA')})`, E);
            setFormula(sheetRow.getCell(6), `SUMIF(${regionMatch},${range('S')})`, FValue);
        } else {
            setFormula(
                sheetRow.getCell(3),
                `SUMIF(${regionMatch},${range('O')})+SUMIF(${regionMatch},${range('AH')})`,
                C
            );
            sheetRow.getCell(4).value = 0;
            formatNumeric(sheetRow.getCell(4));
            setFormula(sheetRow.getCell(5), `SUMIF(${regionMatch},${range('AB')})`, E);
            sheetRow.getCell(6).value = 0;
            formatNumeric(sheetRow.getCell(6));
        }
        const regionBaseMatch = `${range('N')},"${group.region}",${range('AD')},"${group.base}"`;
        setFormula(
            sheetRow.getCell(7),
            `-SUMIFS(${range('Y')},${regionBaseMatch})+SUMIFS(${range('AE')},${regionBaseMatch})`,
            G
        );
        setFormula(
            sheetRow.getCell(8),
            `-SUMIFS(${range('Z')},${regionBaseMatch})+SUMIFS(${range('AF')},${regionBaseMatch})`,
            H
        );
        setFormula(sheetRow.getCell(9), `C${r}+E${r}+G${r}`, I);
        setFormula(sheetRow.getCell(10), `D${r}+F${r}+H${r}`, J);

        memberValuesByRow.set(r, { C, D, E, F: FValue, G, H, I, J });
        const regionRows = memberRowsByRegion.get(group.region) ?? [];
        regionRows.push(r);
        memberRowsByRegion.set(group.region, regionRows);
        allMemberRows.push(r);
        currentRow += 1;
    };

    const writeSubtotalRow = (region: Region, regionRows: number[]): void => {
        const r = currentRow;
        const sheetRow = sheet.getRow(r);
        sheetRow.getCell(1).value = `${region} Total`;
        (['C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'] as (keyof SpecColumns)[]).forEach((column, columnOffset) => {
            const formula = regionRows.map((memberRow) => `${column}${memberRow}`).join('+');
            setFormula(sheetRow.getCell(3 + columnOffset), formula, sumMemberColumns(regionRows, column));
        });
        sheetRow.font = { bold: true };
        currentRow += 1;
    };

    REGION_ORDER.forEach((region) => {
        const regionGroups = groups.filter((group) => group.region === region);
        if (regionGroups.length === 0) {
            return;
        }
        regionGroups.forEach(writeMemberRow);
        writeSubtotalRow(region, memberRowsByRegion.get(region) ?? []);
    });

    if (allMemberRows.length > 0) {
        const totalRow = sheet.getRow(currentRow);
        totalRow.getCell(1).value = 'Total';
        (['C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'] as (keyof SpecColumns)[]).forEach((column, columnOffset) => {
            const formula = allMemberRows.map((memberRow) => `${column}${memberRow}`).join('+');
            setFormula(totalRow.getCell(3 + columnOffset), formula, sumMemberColumns(allMemberRows, column));
        });
        totalRow.font = { bold: true };
    }
};

/**
 * Builds the two-sheet Sales Journal (KMD) workbook from the report response and triggers a download.
 *
 * Invoice rows and received-advance receipt rows share the same normalized shape, so they concatenate into
 * one data range that feeds both sheets: receipt rows carry only their AE/AF net/VAT (every invoice raw input
 * zeroed) and a "PREPAYMENT" type, which the rewired display formulas, the SUMMARY "Prepayments" row and the
 * Specification advances cells pick up. When `prepaymentReceipts` is empty the output matches the base report.
 */
export const exportSalesJournalXlsx = async (
    response: SalesJournalResponse,
    periodStartDate: string,
    periodEndDate: string
): Promise<void> => {
    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    // Each formula cell caches its exact JS-computed result so a non-recalculating spreadsheet app displays
    // the correct value; this flag additionally asks recalculating apps to refresh on open.
    workbook.calcProperties.fullCalcOnLoad = true;

    const rows = [...response.invoices.map(normalizeRow), ...(response.prepaymentReceipts ?? []).map(normalizeReceipt)];
    const periodLabel = `${formatPeriodDate(periodStartDate)} - ${formatPeriodDate(periodEndDate)}`;
    const dataLast = DATA_FIRST + rows.length - 1;

    const computed = buildSalesJournalSheet(workbook, rows, periodLabel);
    buildSpecificationSheet(workbook, computed, dataLast, periodLabel);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `Sales Journal KMD ${periodStartDate}_${periodEndDate}.xlsx`);
};
